import { useState } from "react";
import { Database, BarChart3, FolderOpen, Shield, Plus, Minus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import connectDataAnimation from "@/assets/sustainwize-connect-data.gif";
import trackImpactAnimation from "@/assets/sustainwize-track-impact.gif";
import manageDocumentAnimation from "@/assets/sustainwize-manage-document.gif";
import standardsManagementAnimation from "@/assets/sustainwize-standards-management.gif";

// Preload all animations on module load
const animationSources = [connectDataAnimation, trackImpactAnimation, manageDocumentAnimation, standardsManagementAnimation];
animationSources.forEach(src => {
  const img = new Image();
  img.src = src;
});

const SustainwizeHowItWorksSection = () => {
  const { t } = useLanguage();
  const [expandedStep, setExpandedStep] = useState<number>(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => new Set([...prev, index]));
  };

  const steps = [
    {
      number: "01",
      icon: Database,
      titleKey: "sustainwize.step1Title",
      descKey: "sustainwize.step1Desc",
      animation: connectDataAnimation
    },
    {
      number: "02",
      icon: BarChart3,
      titleKey: "sustainwize.step2Title",
      descKey: "sustainwize.step2Desc",
      animation: trackImpactAnimation
    },
    {
      number: "03",
      icon: FolderOpen,
      titleKey: "sustainwize.step3Title",
      descKey: "sustainwize.step3Desc",
      animation: manageDocumentAnimation
    },
    {
      number: "04",
      icon: Shield,
      titleKey: "sustainwize.step4Title",
      descKey: "sustainwize.step4Desc",
      animation: standardsManagementAnimation
    }
  ];

  const handleStepClick = (index: number) => {
    setExpandedStep(index);
  };

  return (
    <section id="how-it-works" className="py-24 lg:py-32 px-2">
      <div className="bg-[#373530] rounded-[31px] relative overflow-hidden py-16 lg:py-24 px-6 sm:px-8 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="relative z-10">
            {/* Centered Section Header */}
            <div className="text-center mb-12 lg:mb-16">
              <p className="text-sm sm:text-base lg:text-[18px] font-normal text-white mb-6 lg:mb-8 max-w-[1248px] mx-auto px-4">
                {t("sustainwize.howItWorksTitle")}
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[48px] leading-relaxed lg:leading-[1.4] max-w-[1248px] mx-auto font-bold flex flex-col gap-1 lg:gap-2 px-4">
                <span className="text-[#fd6158]">{t("sustainwize.howItWorksSubtitle1")}</span>
                <span className="text-white">{t("sustainwize.howItWorksSubtitle2")}</span>
                <span className="text-white">{t("sustainwize.howItWorksSubtitle3")}</span>
              </h2>
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
              {/* Left Column - Accordion */}
              <div>
                <div className="space-y-0">
                  {steps.map((step, index) => (
                    <div key={step.number} className="border-t border-white/10 last:border-b">
                      <button
                        onClick={() => handleStepClick(index)}
                        className="w-full flex items-center justify-between py-5 text-left group transition-colors duration-300 hover:bg-white/5"
                      >
                        <div className="flex items-center gap-4">
                          <span className={`text-lg font-bold transition-colors duration-300 ${expandedStep === index ? 'text-[#fd6158]' : 'text-white/40'}`}>
                            {step.number}
                          </span>
                          <h3 className={`text-lg lg:text-xl font-medium transition-colors duration-300 ${expandedStep === index ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                            {t(step.titleKey)}
                          </h3>
                        </div>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${expandedStep === index ? 'text-[#fd6158]' : 'text-[#fd6158] group-hover:bg-[#fd6158]/10'}`}>
                          {expandedStep === index ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </div>
                      </button>
                      
                      {/* Expanded Content */}
                      <div className={`overflow-hidden transition-all duration-500 ease-out ${expandedStep === index ? 'max-h-40 opacity-100 pb-5' : 'max-h-0 opacity-0'}`}>
                        <p className="text-white/60 leading-relaxed pl-10">
                          {t(step.descKey)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Visual Display */}
              <div className="relative lg:sticky lg:top-24">
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#fd6158]/20 via-[#fd6158]/5 to-transparent rounded-3xl blur-2xl opacity-60" />
                
                {/* Visual Container */}
                <div className={`relative overflow-hidden ${expandedStep === 2 ? 'aspect-[4/3]' : 'aspect-[16/9] rounded-2xl border border-white/10'}`} style={{
                  background: expandedStep === 2 ? 'transparent' : 'linear-gradient(to bottom right, rgba(253, 97, 88, 0.2), rgba(55, 53, 48, 0.5))'
                }}>
                  {/* Render all animations with opacity transitions */}
                  {steps.map((step, index) => (
                    <div
                      key={step.number}
                      className={`absolute inset-0 transition-opacity duration-500 ease-out ${expandedStep === index ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                    >
                      {!loadedImages.has(index) && expandedStep === index && (
                        <div className="absolute inset-0 bg-white/10 animate-pulse rounded-2xl" />
                      )}
                      <img
                        src={step.animation}
                        alt={t(step.titleKey)}
                        loading="eager"
                        onLoad={() => handleImageLoad(index)}
                        className={`w-full h-full ${index === 2 ? 'object-contain scale-110' : 'object-cover'} transition-opacity duration-300 ${loadedImages.has(index) ? 'opacity-100' : 'opacity-0'}`}
                      />
                    </div>
                  ))}
                  
                  {/* Overlay gradient - hidden for step 03 */}
                  {expandedStep !== 2 && (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#373530]/80 via-transparent to-transparent pointer-events-none z-20" />
                  )}
                  
                  {/* Step Badge and Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none z-30">
                    <div className="animate-fade-in">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fd6158]/20 border border-[#fd6158]/30 mb-3">
                        <span className="text-xs font-medium text-[#fd6158]">Step {steps[expandedStep].number}</span>
                      </span>
                      <p className="text-xl font-bold text-white">
                        {t(steps[expandedStep].titleKey)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SustainwizeHowItWorksSection;
